import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { listBlobs, deleteBlob, BlobItem } from '../api/azureLibrary';
import {
  getUserTrash,
  restoreFromTrash,
  permanentlyDelete,
  emptyTrash,
  getTrashStats,
  getDaysUntilExpiry,
  formatDeletedAt,
  bulkRestore,
  bulkPermanentlyDelete,
  cleanupExpiredItems,
  TrashItem,
} from '../services/trashService';
import toast from '../utils/toastUtils';

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function getFileIcon(fileName: string) {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext || '')) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['mp4', 'mov', 'avi', 'mkv'].includes(ext || '')) return '🎥';
  if (['mp3', 'wav', 'flac'].includes(ext || '')) return '🎵';
  if (['zip', 'rar', '7z'].includes(ext || '')) return '🗜️';
  return '📁';
}

export default function Trash() {
  const { theme } = useTheme();
  const { isMobile } = useMediaQuery();
  const { userId } = useAuth();
  const navigate = useNavigate();
  const [trashItems, setTrashItems] = useState<{ [key: string]: TrashItem }>({});
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [stats, setStats] = useState({ totalItems: 0, totalSize: 0, expiringSoon: 0 });

  useEffect(() => {
    loadTrash();
  }, [userId]);

  function loadTrash() {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      // Auto-cleanup expired items first
      const expired = cleanupExpiredItems(userId);
      if (expired.length > 0) {
        toast.info(`🗑️ Auto-deleted ${expired.length} expired item${expired.length > 1 ? 's' : ''}`);
      }

      const items = getUserTrash(userId);
      setTrashItems(items);
      
      const trashStats = getTrashStats(userId);
      setStats(trashStats);
    } catch (error) {
      console.error('Error loading trash:', error);
      toast.error('Failed to load trash');
    } finally {
      setLoading(false);
    }
  }

  async function handleRestore(fileName: string) {
    if (!userId) return;

    try {
      // Restore in Azure is handled by simply removing from trash metadata
      // The file was never actually deleted from Azure
      restoreFromTrash(userId, fileName);
      
      toast.success(`✅ File restored successfully`);
      loadTrash();
    } catch (error) {
      console.error('Error restoring file:', error);
      toast.error('Failed to restore file');
    }
  }

  async function handlePermanentDelete(fileName: string) {
    if (!userId) return;

    try {
      // Actually delete from Azure
      await deleteBlob(fileName, userId);
      
      // Remove from trash metadata
      permanentlyDelete(userId, fileName);
      
      toast.success('🗑️ File permanently deleted');
      loadTrash();
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error permanently deleting:', error);
      toast.error('Failed to delete file');
    }
  }

  async function handleEmptyTrash() {
    if (!userId) return;

    try {
      const items = Object.keys(trashItems);
      
      // Delete all files from Azure
      for (const fileName of items) {
        await deleteBlob(fileName, userId);
      }
      
      // Clear trash metadata
      const count = emptyTrash(userId);
      
      toast.success(`🗑️ Trash emptied (${count} items deleted)`);
      loadTrash();
      setConfirmEmpty(false);
    } catch (error) {
      console.error('Error emptying trash:', error);
      toast.error('Failed to empty trash');
    }
  }

  async function handleBulkRestore() {
    if (!userId || selectedItems.size === 0) return;

    try {
      const fileNames = Array.from(selectedItems);
      const restored = bulkRestore(userId, fileNames);
      
      toast.success(`✅ ${restored} file${restored > 1 ? 's' : ''} restored`);
      setSelectedItems(new Set());
      loadTrash();
    } catch (error) {
      console.error('Error bulk restoring:', error);
      toast.error('Failed to restore files');
    }
  }

  async function handleBulkDelete() {
    if (!userId || selectedItems.size === 0) return;

    if (!window.confirm(`Permanently delete ${selectedItems.size} file${selectedItems.size > 1 ? 's' : ''}? This cannot be undone.`)) {
      return;
    }

    try {
      const fileNames = Array.from(selectedItems);
      
      // Delete from Azure
      for (const fileName of fileNames) {
        await deleteBlob(fileName, userId);
      }
      
      // Remove from trash metadata
      const deleted = bulkPermanentlyDelete(userId, fileNames);
      
      toast.success(`🗑️ ${deleted} file${deleted > 1 ? 's' : ''} permanently deleted`);
      setSelectedItems(new Set());
      loadTrash();
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Failed to delete files');
    }
  }

  function toggleSelection(fileName: string) {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(fileName)) {
      newSelected.delete(fileName);
    } else {
      newSelected.add(fileName);
    }
    setSelectedItems(newSelected);
  }

  const trashItemsArray = Object.entries(trashItems);

  if (loading) {
    return (
      <div style={{ padding: '20px' }}>
        <PageHeader title="Trash" subtitle="Manage deleted files" />
        <div style={{ textAlign: 'center', padding: '60px', color: theme.colors.textSecondary }}>
          Loading trash...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <PageHeader
        title="🗑️ Trash"
        subtitle="Items are permanently deleted after 30 days"
      />

      {/* Stats Bar */}
      <div style={{
        background: theme.colors.bgSecondary,
        padding: '16px 20px',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: `1px solid ${theme.colors.border}`
      }}>
        <div style={{ display: 'flex', gap: '32px' }}>
          <div>
            <div style={{ color: theme.colors.textSecondary, fontSize: '12px' }}>Items in Trash</div>
            <div style={{ color: theme.colors.textPrimary, fontSize: '20px', fontWeight: 600 }}>
              {stats.totalItems}
            </div>
          </div>
          <div>
            <div style={{ color: theme.colors.textSecondary, fontSize: '12px' }}>Storage Used</div>
            <div style={{ color: theme.colors.textPrimary, fontSize: '20px', fontWeight: 600 }}>
              {formatFileSize(stats.totalSize)}
            </div>
          </div>
          {stats.expiringSoon > 0 && (
            <div>
              <div style={{ color: theme.colors.warning, fontSize: '12px' }}>Expiring Soon</div>
              <div style={{ color: theme.colors.warning, fontSize: '20px', fontWeight: 600 }}>
                {stats.expiringSoon}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          {selectedItems.size > 0 && (
            <>
              <Button variant="secondary" onClick={handleBulkRestore}>
                ↩️ Restore ({selectedItems.size})
              </Button>
              <button
                onClick={handleBulkDelete}
                style={{
                  padding: '10px 20px',
                  background: theme.colors.error,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                🗑️ Delete Forever ({selectedItems.size})
              </button>
            </>
          )}
          {trashItemsArray.length > 0 && selectedItems.size === 0 && (
            <button
              onClick={() => setConfirmEmpty(true)}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: `1px solid ${theme.colors.error}`,
                borderRadius: '8px',
                color: theme.colors.error,
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${theme.colors.error}15`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Empty Trash
            </button>
          )}
        </div>
      </div>

      {/* Empty State */}
      {trashItemsArray.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '80px 20px',
          color: theme.colors.textSecondary
        }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🗑️</div>
          <h2 style={{ color: theme.colors.textPrimary, marginBottom: '8px' }}>Trash is Empty</h2>
          <p>Deleted files will appear here and be kept for 30 days</p>
          <Button
            variant="secondary"
            onClick={() => navigate('/library')}
            style={{ marginTop: '20px' }}
          >
            ← Back to Library
          </Button>
        </div>
      )}

      {/* Trash Items Grid */}
      {trashItemsArray.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: isMobile ? '12px' : '20px'
        }}>
          {trashItemsArray.map(([fileName, item]) => {
            const isSelected = selectedItems.has(fileName);
            const daysLeft = getDaysUntilExpiry(item.expiresAt);
            const isExpiringSoon = daysLeft <= 7;

            return (
              <div
                key={fileName}
                style={{
                  background: theme.colors.bgSecondary,
                  borderRadius: '12px',
                  padding: '20px',
                  border: isSelected 
                    ? `2px solid ${theme.colors.brand}` 
                    : `1px solid ${theme.colors.border}`,
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onClick={() => toggleSelection(fileName)}
              >
                {/* Icon and Selection */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontSize: '48px' }}>
                    {getFileIcon(fileName)}
                  </div>
                  {isSelected && (
                    <div style={{
                      background: theme.colors.brand,
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '16px'
                    }}>
                      ✓
                    </div>
                  )}
                </div>

                {/* File Info */}
                <div style={{
                  color: theme.colors.textPrimary,
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }} title={fileName.split('/').pop()}>
                  {fileName.split('/').pop()}
                </div>

                <div style={{
                  color: theme.colors.textSecondary,
                  fontSize: '12px',
                  marginBottom: '4px'
                }}>
                  {formatFileSize(item.size)}
                </div>

                <div style={{
                  color: theme.colors.textTertiary,
                  fontSize: '12px',
                  marginBottom: '12px'
                }}>
                  Deleted {formatDeletedAt(item.deletedAt)}
                </div>

                {/* Expiry Warning */}
                <div style={{
                  padding: '8px',
                  background: isExpiringSoon 
                    ? `${theme.colors.warning}15` 
                    : `${theme.colors.bgPrimary}`,
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontSize: '12px',
                  color: isExpiringSoon ? theme.colors.warning : theme.colors.textSecondary
                }}>
                  {isExpiringSoon ? '⚠️' : '⏱️'} Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginTop: '12px'
                }} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleRestore(fileName)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      background: theme.colors.brand,
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    ↩️ Restore
                  </button>
                  <button
                    onClick={() => setConfirmDelete(fileName)}
                    style={{
                      padding: '8px 12px',
                      background: 'transparent',
                      border: `1px solid ${theme.colors.error}`,
                      borderRadius: '6px',
                      color: theme.colors.error,
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                    title="Delete Forever"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty Trash Confirmation */}
      {confirmEmpty && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
          onClick={() => setConfirmEmpty(false)}
        >
          <div
            style={{
              background: theme.colors.bgSecondary,
              padding: '32px',
              borderRadius: '12px',
              maxWidth: '400px',
              border: `1px solid ${theme.colors.border}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: theme.colors.textPrimary, marginBottom: '12px' }}>
              ⚠️ Empty Trash?
            </h2>
            <p style={{ color: theme.colors.textSecondary, marginBottom: '20px' }}>
              This will permanently delete all {stats.totalItems} items in trash.
              This action cannot be undone.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setConfirmEmpty(false)}>
                Cancel
              </Button>
              <button
                onClick={handleEmptyTrash}
                style={{
                  padding: '10px 20px',
                  background: theme.colors.error,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Empty Trash
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation */}
      {confirmDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
          onClick={() => setConfirmDelete(null)}
        >
          <div
            style={{
              background: theme.colors.bgSecondary,
              padding: '32px',
              borderRadius: '12px',
              maxWidth: '400px',
              border: `1px solid ${theme.colors.border}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: theme.colors.textPrimary, marginBottom: '12px' }}>
              ⚠️ Delete Forever?
            </h2>
            <p style={{ color: theme.colors.textSecondary, marginBottom: '12px' }}>
              This file will be permanently deleted and cannot be recovered.
            </p>
            <p style={{ color: theme.colors.textPrimary, fontWeight: 600, marginBottom: '20px' }}>
              "{confirmDelete.split('/').pop()}"
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <button
                onClick={() => handlePermanentDelete(confirmDelete)}
                style={{
                  padding: '10px 20px',
                  background: theme.colors.error,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
